import type React from 'react';
import { useId } from 'react';

import {
  Divider,
  FieldSet,
  FormField,
  Input,
  Label,
  LabeledControl,
  Legend,
  Paragraph,
  Radio,
} from '@zillow/constellation-10';

import type { Section } from '../../../../models/index';
import { LANDSCAPING } from '../../../../constants';
import HeaderToggle from '../Utilities/HeaderToggle';
import RadioListBox from '../../../Widgets/RadioListBox';
import { UTILITIES_HEADING_AND_CONTENT } from '../../../../constants/stringConstants';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
  customOption?: React.ReactNode;
};

const INITIAL_STATE = {
  [LANDSCAPING.FIELD_NAME.IS_ENABLED]: true,
  [LANDSCAPING.FIELD_NAME.SERVICES]: '',
  [LANDSCAPING.FIELD_NAME.VARIABLE_COST]: false,
  [LANDSCAPING.FIELD_NAME.NO_ADDITIONAL_FEES]: false,
};

export default function Landscaping_v1({ section, setSectionValues, customOption }: Props): JSX.Element {
  const radioGroupName = useId();
  const variableCostId = useId();
  const noAdditionalFeeId = useId();
  const landscaping_isEnabled = section.getFieldValue(LANDSCAPING.FIELD_NAME.IS_ENABLED);
  const option2Selected = section.getFieldValue(LANDSCAPING.FIELD_NAME.LANDLORD_RESPONSIBILITY);
  const option1Content = 'Responsible for reasonable upkeep including lawn mowing and leaf raking';
  const option2Content = 'Responsible for reasonable upkeep including lawn mowing and leaf raking';

  function toggleUtility(): void {
    setSectionValues({
      ...INITIAL_STATE,
      [LANDSCAPING.FIELD_NAME.IS_ENABLED]: !section.getFieldValue(LANDSCAPING.FIELD_NAME.IS_ENABLED),
    });
  }

  function setServices(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    setSectionValues({
      [LANDSCAPING.FIELD_NAME.SERVICES]: event.currentTarget.value,
    });
  }

  function setVariableCostSelected(): void {
    setSectionValues({
      [LANDSCAPING.FIELD_NAME.NO_ADDITIONAL_FEES]: false,
      [LANDSCAPING.FIELD_NAME.VARIABLE_COST]: true,
    });
  }

  function setNoAdditionalFeeSelected(): void {
    setSectionValues({
      [LANDSCAPING.FIELD_NAME.NO_ADDITIONAL_FEES]: true,
      [LANDSCAPING.FIELD_NAME.VARIABLE_COST]: false,
    });
  }

  function renderOption2Details(): React.ReactNode {
    return (
      <>
        <FormField
          required
          css={{ marginTop: 'layout.tight', marginBottom: 'layout.tight' }}
          label={<Label>What services will be provided?</Label>}
          control={<Input value={section.getFieldValue(LANDSCAPING.FIELD_NAME.SERVICES)} onChange={setServices} />}
        />
        {section.getField(LANDSCAPING.FIELD_NAME.VARIABLE_COST) &&
          section.getField(LANDSCAPING.FIELD_NAME.NO_ADDITIONAL_FEES) && (
            <FieldSet required legend={<Legend>What is the additional fee for landscaping?</Legend>}>
              <LabeledControl
                controlId={variableCostId}
                label={
                  <Label>
                    Variable monthly cost
                    <Paragraph>Landlord bills tenant for actual cost</Paragraph>
                  </Label>
                }
                size="lg"
                control={
                  <Radio
                    aria-label="Variable monthly cost"
                    name={radioGroupName}
                    checked={section.getFieldValue(LANDSCAPING.FIELD_NAME.VARIABLE_COST)}
                    onChange={setVariableCostSelected}
                  />
                }
                disabled={section.isFieldDisabled(LANDSCAPING.FIELD_NAME.VARIABLE_COST)}
              />
              <LabeledControl
                controlId={noAdditionalFeeId}
                label={<Label>No additional fee for landscaping</Label>}
                size="lg"
                control={
                  <Radio
                    aria-label="No additional fee for landscaping"
                    name={radioGroupName}
                    checked={section.getFieldValue(LANDSCAPING.FIELD_NAME.NO_ADDITIONAL_FEES)}
                    onChange={setNoAdditionalFeeSelected}
                  />
                }
                disabled={section.isFieldDisabled(LANDSCAPING.FIELD_NAME.NO_ADDITIONAL_FEES)}
              />
            </FieldSet>
          )}
      </>
    );
  }

  return (
    <>
      <HeaderToggle
        header="Landscaping"
        subheader="Applies to this property"
        switchOn={landscaping_isEnabled}
        onSwitchToggle={toggleUtility}
      />
      {landscaping_isEnabled && (
        <>
          <Divider css={{ marginTop: 'layout.tighter', marginBottom: 'layout.tighter' }} />
          {customOption ? (
            customOption
          ) : (
            <>
              <FieldSet required legend={<Legend>Who will be responsible for landscaping?</Legend>}>
                <RadioListBox
                  section={section}
                  setSectionValues={setSectionValues}
                  option1FieldName={LANDSCAPING.FIELD_NAME.TENANT_RESPONSIBILITY}
                  option2FieldName={LANDSCAPING.FIELD_NAME.LANDLORD_RESPONSIBILITY}
                  option1Header={UTILITIES_HEADING_AND_CONTENT.option1Heading}
                  option1Content={option1Content}
                  option2Header={UTILITIES_HEADING_AND_CONTENT.option2Heading}
                  option2Content={option2Content}
                  initialState={INITIAL_STATE}
                />
              </FieldSet>
              {option2Selected && renderOption2Details()}
            </>
          )}
        </>
      )}
    </>
  );
}
